// Empty titles also stop a parent's native tooltip from being inherited.
export function suppressNativeTitles(root) {
  if (!root) return () => {}

  const titles = new Map()
  const options = { subtree: true, childList: true, attributes: true, attributeFilter: ['title'] }
  let active = true

  const canSuppress = element => element.nodeType === 1 &&
    element.localName !== 'iframe' &&
    !(element.namespaceURI === 'http://www.w3.org/2000/svg' && element.localName === 'title')

  function updateAccessibleName(element, saved) {
    if (saved.ariaLabel !== null && element.getAttribute('aria-label') === saved.ariaLabel) {
      saved.ariaLabel = saved.title
      element.setAttribute('aria-label', saved.ariaLabel)
      return
    }

    saved.ariaLabel = null
    const interactive = element.localName === 'button' ||
      element.localName === 'a' ||
      element.getAttribute('role') === 'button'
    if (!interactive || element.hasAttribute('aria-label') || element.hasAttribute('aria-labelledby') ||
      element.textContent.trim()) return

    const namedChild = Array.from(element.querySelectorAll('[aria-label], [aria-labelledby], img[alt]'))
      .some(child => ['aria-label', 'aria-labelledby', 'alt'].some(name => (child.getAttribute(name) || '').trim()))
    if (namedChild) return

    saved.ariaLabel = saved.title
    element.setAttribute('aria-label', saved.ariaLabel)
  }

  function release(element, restoreTitle) {
    const saved = titles.get(element)
    if (!saved) return
    if (restoreTitle && element.getAttribute('title') === '') element.setAttribute('title', saved.title)
    if (saved.ariaLabel !== null && element.getAttribute('aria-label') === saved.ariaLabel) {
      element.removeAttribute('aria-label')
    }
    titles.delete(element)
  }

  function suppress(element, changedByApp = false) {
    if (!canSuppress(element)) return
    const title = element.getAttribute('title')
    if (!title) {
      // A business update to an empty/absent title must not revive an old one.
      if (changedByApp) release(element, false)
      return
    }

    const saved = titles.get(element) || { title, ariaLabel: null }
    saved.title = title
    titles.set(element, saved)
    updateAccessibleName(element, saved)
    element.setAttribute('title', '')
  }

  function visitTitles(node, callback) {
    if (node.nodeType === 1) callback(node)
    if (node.querySelectorAll) node.querySelectorAll('[title]').forEach(callback)
  }

  function visitElements(node, callback) {
    if (node.nodeType !== 1) return
    callback(node)
    if (node.children) Array.from(node.children).forEach(child => visitElements(child, callback))
  }

  function processMutations(records) {
    const changed = new Set()
    const added = new Set()
    const removed = []
    records.forEach(record => {
      if (record.type === 'attributes') changed.add(record.target)
      else {
        record.addedNodes.forEach(node => visitTitles(node, element => added.add(element)))
        record.removedNodes.forEach(node => removed.push(node))
      }
    })

    changed.forEach(element => {
      if (root.contains(element)) suppress(element, true)
      else release(element, false)
    })
    added.forEach(element => {
      if (!changed.has(element) && root.contains(element)) suppress(element)
    })
    removed.forEach(node => visitElements(node, element => {
      // Moves within the observed root retain their original title. Detached
      // subtrees are restored and released so long chats do not retain them.
      if (!root.contains(element)) release(element, true)
    }))
  }

  const observer = new root.ownerDocument.defaultView.MutationObserver(records => {
    // Do not observe our own title writes; otherwise an empty replacement can
    // be mistaken for the app deliberately clearing its title.
    observer.disconnect()
    processMutations(records)
    if (active) observer.observe(root, options)
  })

  visitTitles(root, element => suppress(element))
  observer.observe(root, options)

  return () => {
    if (!active) return
    const pending = observer.takeRecords()
    observer.disconnect()
    active = false
    processMutations(pending)
    titles.forEach((saved, element) => release(element, root.contains(element)))
  }
}
